import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { ConserjeRecipeResult, RecipeDestinationOption } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { platformAdminApi } from '../api/platformAdminApi';
import { useActiveTenants } from './recipe.hooks';
import { ClientFields, Question, RecipeDone, RecipeFooter, RecipePage } from './recipeParts';
import {
  defaultReceptionName,
  defaultWelcome,
  initialDestinations,
  KIND_LABEL,
  MENU_LABEL_MAX,
  type ChosenDestination,
} from './asistenteConserje.utils';

export function ConserjeRecipePage() {
  const [error, setError] = useState('');
  const tenants = useActiveTenants(setError);
  const [tenantId, setTenantId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [agentName, setAgentName] = useState('');
  const [options, setOptions] = useState<RecipeDestinationOption[] | null>(null);
  const [chosen, setChosen] = useState<ChosenDestination[]>([]);
  const [welcome, setWelcome] = useState('');
  const [welcomeTouched, setWelcomeTouched] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState<ConserjeRecipeResult | null>(null);

  const shownWelcome = welcomeTouched ? welcome : defaultWelcome(companyName);

  const loadOptions = (id: string) => {
    setOptions(null);
    setChosen([]);
    void platformAdminApi
      .listRecipeDestinations(id)
      .then(list => {
        setOptions(list);
        setChosen(initialDestinations(list));
      })
      .catch(err => setError(err instanceof Error ? err.message : 'No se pudieron cargar los agentes del cliente'));
  };

  const toggle = (option: RecipeDestinationOption) =>
    setChosen(current =>
      current.some(item => item.botId === option.botId)
        ? current.filter(item => item.botId !== option.botId)
        : [...current, { botId: option.botId, menuLabel: option.suggestedLabel }],
    );

  const rename = (botId: string, menuLabel: string) =>
    setChosen(current => current.map(item => (item.botId === botId ? { ...item, menuLabel } : item)));

  const answered = [
    Boolean(tenantId && companyName.trim()),
    chosen.length > 0 && chosen.every(item => item.menuLabel.trim()),
    Boolean(shownWelcome.trim()),
  ];
  const ready = answered.every(Boolean);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || progress) return;
    setError('');
    setProgress('Creando el Conserje');
    try {
      // Mismo orden que el listado: así se ve el menú.
      const ordered = (options ?? [])
        .map(option => chosen.find(item => item.botId === option.botId))
        .filter((item): item is ChosenDestination => Boolean(item));
      setResult(
        await platformAdminApi.createConserje({
          tenantId,
          companyName: companyName.trim(),
          agentName: agentName.trim() || undefined,
          welcomeText: shownWelcome.trim(),
          destinations: ordered.map(item => ({ botId: item.botId, menuLabel: item.menuLabel.trim() })),
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el Conserje');
    } finally {
      setProgress('');
    }
  };

  if (result) {
    return (
      <RecipeDone
        title={`El Conserje de ${companyName} está creado`}
        subtitle='Recibe a todos, entiende qué necesitan y los pasa al agente que corresponde. Conectale el WhatsApp a él, no a cada agente.'
        steps={result.steps}
        aiReady={result.aiReady}
        actions={
          <>
            <Button asChild size='lg'>
              <Link to='/admin/whatsapp'>Conectar WhatsApp</Link>
            </Button>
            <Button asChild size='lg' variant='outline'>
              <Link to={`/admin/chatbot/bots/${result.botId}`}>Ver el Conserje</Link>
            </Button>
          </>
        }
      />
    );
  }

  const menuPreview = (options ?? []).filter(option => chosen.some(item => item.botId === option.botId));

  return (
    <RecipePage
      title='Nuevo Conserje'
      intro='Un recepcionista que saluda, entiende qué necesita cada persona y la pasa al agente que corresponde. Va al frente de los agentes que ya creaste.'
    >
      <form onSubmit={handleSubmit} noValidate>
        <ol className='relative'>
          <Question done={answered[0]} title='¿Para qué cliente?' hint='Cada cliente tiene un solo conserje.'>
            <ClientFields
              tenants={tenants}
              tenantId={tenantId}
              onTenant={tenant => {
                setTenantId(tenant.id);
                setCompanyName(tenant.name);
                loadOptions(tenant.id);
              }}
              companyName={companyName}
              onCompanyName={setCompanyName}
              agentName={agentName}
              onAgentName={setAgentName}
              defaultAgentName={defaultReceptionName(companyName)}
            />
          </Question>

          <Question
            done={answered[1]}
            title='¿A quién deriva?'
            hint='Lo que escribas en cada uno es el botón que ve la persona. Si escribe con sus palabras, el conserje entiende igual.'
          >
            <DestinationList
              tenantChosen={Boolean(tenantId)}
              options={options}
              chosen={chosen}
              onToggle={toggle}
              onRename={rename}
            />
          </Question>

          <Question done={answered[2]} last title='¿Cómo saluda?' hint='Lo primero que lee la persona.'>
            <Label htmlFor='welcome' className='sr-only'>
              Saludo
            </Label>
            <Textarea
              id='welcome'
              rows={3}
              maxLength={2000}
              value={shownWelcome}
              onChange={event => {
                setWelcomeTouched(true);
                setWelcome(event.target.value);
              }}
            />
            {menuPreview.length > 0 ? (
              <figure className='mt-4 rounded-lg bg-muted/50 p-4'>
                <figcaption className='mb-3 text-sm text-muted-foreground'>Así lo va a ver la persona:</figcaption>
                <p className='w-fit max-w-[85%] rounded-2xl rounded-tl-sm bg-background px-3 py-2 text-sm text-foreground shadow-sm'>
                  {shownWelcome}
                </p>
                <ul className='mt-2 flex flex-wrap gap-2' aria-label='Menú del conserje'>
                  {menuPreview.map(option => (
                    <li
                      key={option.botId}
                      className='rounded-full border border-primary/40 bg-background px-3 py-1 text-sm text-primary'
                    >
                      {chosen.find(item => item.botId === option.botId)?.menuLabel || option.suggestedLabel}
                    </li>
                  ))}
                </ul>
              </figure>
            ) : null}
          </Question>
        </ol>

        <RecipeFooter error={error} progress={progress} answered={answered} submitLabel='Crear Conserje' />
      </form>
    </RecipePage>
  );
}

function DestinationList(props: {
  tenantChosen: boolean;
  options: RecipeDestinationOption[] | null;
  chosen: ChosenDestination[];
  onToggle: (option: RecipeDestinationOption) => void;
  onRename: (botId: string, label: string) => void;
}) {
  if (!props.tenantChosen) {
    return <p className='text-sm text-muted-foreground'>Elegí primero el cliente.</p>;
  }
  if (props.options === null) {
    return <p className='text-sm text-muted-foreground'>Buscando los agentes del cliente…</p>;
  }
  if (props.options.length === 0) {
    return (
      <p className='rounded-lg bg-muted/50 px-4 py-3 text-sm text-foreground'>
        Este cliente todavía no tiene agentes a los que derivar.{' '}
        <Link to='/admin/recetas' className='font-medium text-primary underline-offset-4 hover:underline'>
          Creá primero un Asistente, Cobrador, Vendedor o Mesa de ayuda
        </Link>
        .
      </p>
    );
  }
  return (
    <ul className='divide-y divide-border rounded-lg border border-border' aria-label='Agentes del cliente'>
      {props.options.map(option => {
        const pick = props.chosen.find(item => item.botId === option.botId);
        const inputId = `label-${option.botId}`;
        return (
          <li key={option.botId} className='flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center'>
            <label className='flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-sm'>
              <input
                type='checkbox'
                className='h-4 w-4 accent-primary'
                checked={Boolean(pick)}
                onChange={() => props.onToggle(option)}
              />
              <span className='min-w-0'>
                <span className='block truncate font-medium text-foreground'>{option.name}</span>
                <span className='block text-muted-foreground'>{KIND_LABEL[option.kind]}</span>
              </span>
            </label>
            {pick ? (
              <div className='sm:w-52'>
                <Label htmlFor={inputId} className='sr-only'>
                  Texto del botón para {option.name}
                </Label>
                <Input
                  id={inputId}
                  value={pick.menuLabel}
                  maxLength={MENU_LABEL_MAX}
                  aria-invalid={!pick.menuLabel.trim()}
                  onChange={event => props.onRename(option.botId, event.target.value)}
                />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
